import { useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { API_BASE_URL } from "../config/api";
import PremiumProducts from "../Components/PremiumProducts";
import FilterModal, { EMPTY_FILTER } from "../Components/FilterModal";
import Seo from "../Components/Seo";

const CATEGORY_ICONS = [
    { keywords: ["mobile", "smartphone"], colors: ["#2A6B8", "#3B8FD9"], letter: "📱" },
    { keywords: ["electronics", "devices"], colors: ["#E86A33", "#F2994A"], letter: "💻" },
    { keywords: ["car"], colors: ["#2E7D32", "#66BB6A"], letter: "🚗" },
    { keywords: ["bike"], colors: ["#7B4B94", "#AB6FD6"], letter: "🏍️" },
    { keywords: ["commercial", "shipping"], colors: ["#37474F", "#607D8B"], letter: "🚚" },
    { keywords: ["for sale", "house"], colors: ["#1B5E20", "#388E3C"], letter: "🏠" },
    { keywords: ["for rent", "apartment"], colors: ["#0D47A1", "#1976D2"], letter: "🏢" },
    { keywords: ["lands", "plot"], colors: ["#4E342E", "#A1887F"], letter: "🏞️" },
    { keywords: ["job"], colors: ["#B71C1C", "#E53935"], letter: "💼" },
    { keywords: ["furniture"], colors: ["#A0682C", "#D4A06A"], letter: "🛋️" },
    { keywords: ["fashion"], colors: ["#AD1457", "#F06292"], letter: "👗" },
    { keywords: ["book"], colors: ["#6A1B9A", "#AB47BC"], letter: "📚" },
    { keywords: ["pets"], colors: ["#00695C", "#26A69A"], letter: "🐾" },
    { keywords: ["other"], colors: ["#424242", "#757575"], letter: "📦" },
];

const getCategoryStyle = (name) => {
    const match = CATEGORY_ICONS.find((c) =>
        c.keywords.some((k) => (name || "").toLowerCase().includes(k))
    );
    return match || { colors: ["#616161", "#9E9E9E"], letter: "📦" };
};

const Home = () => {
    const navigate = useNavigate();
    const [allProducts, setAllProducts] = useState([]);
    const [banners, setBanners] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [errorProducts, setErrorProducts] = useState(null);
    const [search, setSearch] = useState("");
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [heroIndex, setHeroIndex] = useState(0);
    const [filterOpen, setFilterOpen] = useState(false);
    const [activeFilter, setActiveFilter] = useState({ ...EMPTY_FILTER });

    // --- Fetch ---
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const userMobile = localStorage.getItem("userMobileNumber") || "";
                const res = await fetch(`${API_BASE_URL}/Product/GetAllProducts?mobileNo=${encodeURIComponent(userMobile)}`);
                if (!res.ok) throw new Error("Failed");
                const data = await res.json();
                if (!cancelled) setAllProducts(Array.isArray(data) ? data.filter((p) => !p.inBin) : []);
            } catch (e) {
                if (!cancelled) setErrorProducts(e.message);
            } finally {
                if (!cancelled) setLoadingProducts(false);
            }
        };
        load();
        const loadCats = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/Category/GetCategories`);
                const data = await res.json();
                if (!cancelled && Array.isArray(data)) setCategories(data);
            } catch {}
        };
        loadCats();
        const loadBanners = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/BannerApi/GetAllBanners`);
                const data = await res.json();
                if (!cancelled && Array.isArray(data)) setBanners(data);
            } catch {}
        };
        loadBanners();
        return () => { cancelled = true; };
    }, []);

    // --- Hero banner auto-slide (4s, app pattern) ---
    const heroBanners = banners
        .map((b) => {
            const img = b.bannerImage || b.BannerImage || "";
            return img ? { img, name: b.name || b.Name || "" } : null;
        })
        .filter(Boolean);

    useEffect(() => {
        if (heroBanners.length <= 1) return;
        const id = setInterval(() => setHeroIndex((i) => (i + 1) % heroBanners.length), 4000);
        return () => clearInterval(id);
    }, [heroBanners.length]);

    // --- Filtered products ---
    const filterActive =
        activeFilter.categoryId || activeFilter.subCategoryId || activeFilter.cityId ||
        activeFilter.min > 0 || activeFilter.max < Number.MAX_SAFE_INTEGER;

    const displayProducts = useMemo(() => {
        const term = search.trim().toLowerCase();
        return allProducts.filter((p) => {
            const catMatch = selectedCategoryId
                ? String(p.CategoryId) === String(selectedCategoryId)
                : true;
            const textMatch = term
                ? (p.ProductName || "").toLowerCase().includes(term)
                : true;
            const price = Number(p.Price) || 0;
            const priceMatch = price >= (activeFilter.min || 0) && price <= (activeFilter.max ?? Number.MAX_SAFE_INTEGER);
            const fCatMatch = activeFilter.categoryId
                ? String(p.CategoryId) === String(activeFilter.categoryId)
                : true;
            const fSubMatch = activeFilter.subCategoryId
                ? String(p.SubCategoryId) === String(activeFilter.subCategoryId)
                : true;
            const fCityMatch = activeFilter.cityId
                ? String(p.CityId) === String(activeFilter.cityId)
                : true;
            return catMatch && textMatch && priceMatch && fCatMatch && fSubMatch && fCityMatch;
        });
    }, [allProducts, search, selectedCategoryId, activeFilter]);

    return (
        <div style={{ minHeight: "100vh", background: "#f8f9fa" }}>
            <Seo
                title="Free Online Classified Marketplace"
                description="India ka best free classified platform. Mobile, Electronics, Cars, Bikes, Furniture aur Jobs — bechein ya khareedein 100% free."
                path="/"
            />
            {/* ===== HERO BANNER SLIDER ===== */}
            {heroBanners.length > 0 && (
                <section
                    style={{
                        margin: "12px",
                        position: "relative",
                        height: "clamp(240px, 34vw, 480px)",
                        borderRadius: "18px",
                        overflow: "hidden",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                        background: "#111",
                    }}
                >
                    {heroBanners.map((b, i) => (
                        <img
                            key={`hero-${i}`}
                            src={b.img}
                            alt={b.name}
                            style={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                opacity: i === heroIndex % heroBanners.length ? 1 : 0,
                                transition: "opacity 600ms ease",
                            }}
                        />
                    ))}
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            background: "linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.1) 60%, transparent 100%)",
                        }}
                    />
                    <div style={{ position: "absolute", bottom: "14px", left: "50%", transform: "translateX(-50%)", display: "flex", gap: "6px" }}>
                        {heroBanners.map((_, i) => (
                            <span
                                key={i}
                                onClick={() => setHeroIndex(i)}
                                style={{
                                    width: i === heroIndex % heroBanners.length ? "20px" : "7px",
                                    height: "7px",
                                    borderRadius: "999px",
                                    background: i === heroIndex % heroBanners.length ? "#fff" : "rgba(255,255,255,0.5)",
                                    cursor: "pointer",
                                    transition: "all 300ms ease",
                                }}
                            />
                        ))}
                    </div>
                </section>
            )}

            {/* ===== SHOP BY CATEGORY (app jaisa) ===== */}
            {categories.length > 0 && (
                <section style={{ marginTop: "12px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px", marginBottom: "8px" }}>
                        <h2 style={{ fontSize: "15px", fontWeight: 700, margin: 0, display: "flex", alignItems: "center", gap: "6px" }}>
                            <span style={{ fontSize: "16px" }}>🏷️</span> Shop by Category
                        </h2>
                        <button
                            onClick={() => navigate("/login")}
                            style={{
                                display: "flex", alignItems: "center", gap: "5px",
                                backgroundColor: "#FFF1DC", borderRadius: "14px",
                                padding: "6px 12px", border: "none", cursor: "pointer",
                                fontSize: "12px", fontWeight: 700, color: "#E86A33",
                            }}
                        >
                            📤 Share & Earn
                        </button>
                    </div>
                    <div
                        style={{
                            display: "flex",
                            gap: "10px",
                            padding: "0 12px",
                            overflowX: "auto",
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                        }}
                    >
                        {categories.map((cat) => {
                            const catName = cat.name || cat.Name || "";
                            const catStyle = getCategoryStyle(catName);
                            const isActive = selectedCategoryId === cat.id;
                            return (
                                <div
                                    key={cat.id}
                                    onClick={() => setSelectedCategoryId(isActive ? null : cat.id)}
                                    style={{
                                        minWidth: "110px",
                                        borderRadius: "14px",
                                        background: isActive
                                            ? `linear-gradient(135deg, ${catStyle.colors[0]}, ${catStyle.colors[1]})`
                                            : "#fff",
                                        border: isActive ? "none" : "1px solid rgba(0,0,0,0.08)",
                                        boxShadow: isActive
                                            ? "0 4px 14px rgba(0,0,0,0.18)"
                                            : "0 2px 6px rgba(0,0,0,0.06)",
                                        cursor: "pointer",
                                        overflow: "hidden",
                                        flexShrink: 0,
                                    }}
                                >
                                    <div
                                        style={{
                                            height: "64px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: "42px",
                                                height: "42px",
                                                borderRadius: "50%",
                                                background: isActive ? "rgba(255,255,255,0.25)" : catStyle.colors[0],
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "20px",
                                                border: "2px solid rgba(255,255,255,0.9)",
                                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                            }}
                                        >
                                            {catStyle.letter}
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            fontSize: "12px",
                                            fontWeight: 700,
                                            color: isActive ? "#fff" : "#1a1a2e",
                                            padding: "8px",
                                            textAlign: "center",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {catName}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* ===== SEARCH ===== */}
            <section style={{ padding: "12px 14px 0" }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        background: "#fff",
                        borderRadius: "12px",
                        padding: "10px 14px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        border: "1px solid #eee",
                    }}
                >
                    <span style={{ fontSize: "16px", opacity: 0.5 }}>🔍</span>
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search products..."
                        style={{
                            flex: 1,
                            border: "none",
                            outline: "none",
                            background: "transparent",
                            fontSize: "14px",
                            color: "#1a1a2e",
                        }}
                    />
                    {(search || selectedCategoryId) && (
                        <button
                            onClick={() => { setSearch(""); setSelectedCategoryId(null); }}
                            style={{
                                background: "#f1f5f9", border: "none", borderRadius: "999px",
                                width: "26px", height: "26px", cursor: "pointer",
                                fontSize: "13px", color: "#64748b",
                            }}
                        >
                            ✕
                        </button>
                    )}
                </div>

                <button
                    onClick={() => setFilterOpen(true)}
                    style={{
                        display: "flex", alignItems: "center", gap: "6px",
                        marginTop: "10px", padding: "9px 16px", borderRadius: "10px",
                        border: filterActive ? "none" : "1px solid #e2e8f0",
                        background: filterActive ? "linear-gradient(135deg,#2563eb,#7c3aed)" : "#fff",
                        color: filterActive ? "#fff" : "#334155",
                        fontWeight: 700, fontSize: "13px", cursor: "pointer",
                        boxShadow: filterActive ? "0 4px 12px rgba(37,99,235,0.3)" : "0 2px 6px rgba(0,0,0,0.05)",
                    }}
                >
                    <span>⚙️</span>
                    {filterActive ? "Filter Applied" : "Filter"}
                    {filterActive && (
                        <span
                            onClick={(e) => { e.stopPropagation(); setActiveFilter({ ...EMPTY_FILTER }); }}
                            style={{
                                marginLeft: "4px", background: "rgba(255,255,255,0.25)",
                                borderRadius: "999px", width: "20px", height: "20px",
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                fontSize: "11px",
                            }}
                        >
                            ✕
                        </span>
                    )}
                </button>
            </section>

            <FilterModal
                isOpen={filterOpen}
                onClose={() => setFilterOpen(false)}
                onApply={(f) => setActiveFilter(f)}
                activeFilter={activeFilter}
            />

            {/* ===== PREMIUM PRODUCTS ===== */}
            {!loadingProducts && allProducts.filter((p) => Number(p.isPremium) === 1).length > 0 && (
              <div style={{ padding: "14px 14px 0" }}>
                <PremiumProducts type="Premium Products" APIdata={allProducts.filter((p) => Number(p.isPremium) === 1)} />
              </div>
            )}

            {/* ===== PRODUCTS HEADING ===== */}
            <div style={{ padding: "10px 14px 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <h2 style={{ fontSize: "15px", fontWeight: 700, margin: 0 }}>
                    All Products
                    <span style={{
                        marginLeft: "8px", fontSize: "11px",
                        background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                        color: "#fff", padding: "2px 8px", borderRadius: "999px", fontWeight: 700,
                    }}>
                        {displayProducts.length}
                    </span>
                </h2>
            </div>

            {/* ===== PRODUCT GRID (catalogue jaisa) ===== */}
            <section style={{ padding: "8px 12px 24px" }}>
                {loadingProducts && (
                    <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8", fontWeight: 600 }}>
                        Loading products...
                    </div>
                )}
                {errorProducts && (
                    <div style={{ textAlign: "center", padding: "40px", color: "#ef4444" }}>{errorProducts}</div>
                )}
                {!loadingProducts && displayProducts.length === 0 && (
                    <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8", fontWeight: 600 }}>
                        No products found.
                    </div>
                )}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                        gap: "12px",
                    }}
                >
                    {displayProducts.map((item) => (
                        <div
                            key={item.Id}
                            onClick={() => navigate(`/product-details/${item.Id}`)}
                            style={{
                                background: "#fff",
                                borderRadius: "12px",
                                overflow: "hidden",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                cursor: "pointer",
                                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translateY(-4px)";
                                e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.12)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                            }}
                        >
                            <img
                                src={item.ProductImage || "/placeholder.png"}
                                alt={item.ProductName}
                                loading="lazy"
                                style={{
                                    width: "100%",
                                    height: "150px",
                                    objectFit: "cover",
                                }}
                            />
                            <div style={{ padding: "10px" }}>
                                <h4 style={{
                                    fontSize: "13px", fontWeight: 600, margin: 0,
                                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                    color: "#1a1a2e",
                                }}>
                                    {item.ProductName}
                                </h4>
                                <span style={{
                                    display: "inline-block",
                                    marginTop: "6px",
                                    fontWeight: 800,
                                    fontSize: "14px",
                                    color: "#16a34a",
                                    background: "#e8f7ee",
                                    padding: "2px 8px",
                                    borderRadius: "999px",
                                }}>
                                    ₹{item.Price}
                                </span>
                                {item.Quantity > 0 && (
                                    <span style={{
                                        display: "inline-block",
                                        marginTop: "6px",
                                        marginLeft: "6px",
                                        fontWeight: 700,
                                        fontSize: "12px",
                                        color: "#475569",
                                        background: "#f1f5f9",
                                        padding: "2px 8px",
                                        borderRadius: "999px",
                                    }}>
                                        Qty: {item.Quantity}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default Home;
